/**
 * useModalRoute Hook
 *
 * Hook for controlling route-based App Bridge modals.
 * Manages open/close state and URL construction with preserved Shopify params.
 *
 * @example
 * ```tsx
 * const modal = useModalRoute('/app/modal/alert-detail');
 * 
 * // Open modal with params
 * <Button onClick={() => modal.open({ alertId: '123' })}>View Details</Button>
 * 
 * // Render modal
 * <AppBridgeModal
 *   src={modal.src}
 *   open={modal.isOpen}
 *   onClose={modal.close}
 * />
 * ```
 */

import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

export interface UseModalRouteOptions {
  /** Base route path (e.g., '/app/modal/alert-detail') */
  baseRoute: string;
  /** Initial open state */
  initialOpen?: boolean;
}

export interface UseModalRouteReturn {
  /** Whether the modal is currently open */
  isOpen: boolean;
  /** The full URL to pass to AppBridgeModal src prop */
  src: string | null;
  /** Open the modal, optionally with additional params */
  open: (params?: Record<string, string>) => void;
  /** Close the modal */
  close: () => void;
  /** Toggle modal state */
  toggle: () => void;
  /** Build a modal URL with current Shopify params + custom params */
  buildUrl: (params?: Record<string, string>) => string;
}

/**
 * Hook for managing route-based modal state with Shopify param preservation
 */
export function useModalRoute(
  baseRoute: string,
  initialOpen = false
): UseModalRouteReturn {
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [modalParams, setModalParams] = useState<Record<string, string> | null>(null);

  // Build URL with preserved Shopify params + custom params
  const buildUrl = useCallback(
    (params?: Record<string, string>) => {
      // Preserve essential Shopify params
      const shopifyParams = new URLSearchParams();
      const essentialParams = ['shop', 'host', 'embedded', 'hmac', 'timestamp', 'locale'];
      
      essentialParams.forEach((key) => {
        const value = searchParams.get(key);
        if (value) {
          shopifyParams.set(key, value);
        }
      });

      // Add custom params
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          shopifyParams.set(key, value);
        });
      }

      const queryString = shopifyParams.toString();
      return `${baseRoute}${queryString ? `?${queryString}` : ''}`;
    },
    [baseRoute, searchParams]
  );

  // Current src URL (null when closed)
  const src = useMemo(() => {
    if (!isOpen) return null;
    return buildUrl(modalParams ?? undefined);
  }, [isOpen, modalParams, buildUrl]);

  const open = useCallback((params?: Record<string, string>) => {
    setModalParams(params ?? null);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    // Clear params after a brief delay (allow closing animation)
    setTimeout(() => setModalParams(null), 150);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) {
        setTimeout(() => setModalParams(null), 150);
      }
      return !prev;
    });
  }, []);

  return {
    isOpen,
    src,
    open,
    close,
    toggle,
    buildUrl,
  };
}

export default useModalRoute;
